import { useState, useEffect, useContext } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import Navbar from "@/components/Navbar";
import { AuthContext } from "@/App";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  ClipboardCheck,
  FileText,
  CheckCircle2,
  AlertCircle,
  Clock,
  XCircle,
  Download,
  Pen,
  Building2,
  Users,
  IndianRupee,
  Calendar,
  Shield,
  FileCheck,
  Loader2,
  ChevronRight,
  Briefcase,
  Scale,
  Settings,
  Package
} from "lucide-react";
import jsPDF from "jspdf";

const CATEGORY_ICONS = {
  financial: IndianRupee,
  legal: Scale,
  operational: Settings,
  assets: Package
};

const CATEGORY_LABELS = {
  financial: "Financial Documents",
  legal: "Legal & Compliance",
  operational: "Operational",
  assets: "Assets & Inventory"
};

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-slate-100 text-slate-600", icon: Clock },
  verified: { label: "Verified", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  flagged: { label: "Flagged", color: "bg-red-100 text-red-700", icon: AlertCircle },
  not_applicable: { label: "N/A", color: "bg-slate-100 text-slate-500", icon: XCircle }
};

export default function DueDiligence() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dealIdFromUrl = searchParams.get("deal");
  
  const [deals, setDeals] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState(dealIdFromUrl || "");
  const [checklist, setChecklist] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("checklist");
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const [signing, setSigning] = useState(false);
  const [creatingChecklist, setCreatingChecklist] = useState(false);
  const [creatingAgreement, setCreatingAgreement] = useState(false);
  
  // Agreement form
  const [agreementForm, setAgreementForm] = useState({
    due_diligence_period_days: 30,
    confidentiality_period_months: 24,
    custom_terms: ""
  });

  useEffect(() => {
    if (user) {
      fetchDeals();
    }
  }, [user]);

  useEffect(() => {
    if (selectedDealId) {
      fetchDealData();
    }
  }, [selectedDealId]);

  const fetchDeals = async () => {
    try {
      const { data } = await api.get("/due-diligence/deals");
      setDeals(data);
      if (data.length > 0 && !selectedDealId) {
        setSelectedDealId(data[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch deals:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDealData = async () => {
    try {
      // Fetch checklist
      try {
        const { data: checklistData } = await api.get(`/due-diligence/checklist/${selectedDealId}`);
        setChecklist(checklistData);
      } catch (e) {
        setChecklist(null);
      }
      
      // Fetch agreement
      try {
        const { data: agreementData } = await api.get(`/due-diligence/agreement/${selectedDealId}`);
        setAgreement(agreementData);
      } catch (e) {
        setAgreement(null);
      }
    } catch (error) {
      console.error("Failed to fetch deal data:", error);
    }
  };

  const createChecklist = async () => {
    setCreatingChecklist(true);
    try {
      const { data } = await api.post("/due-diligence/checklist", { deal_id: selectedDealId });
      setChecklist(data);
      toast.success("Due Diligence Checklist created!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create checklist");
    } finally {
      setCreatingChecklist(false);
    }
  };

  const createAgreement = async () => {
    setCreatingAgreement(true);
    try {
      const { data } = await api.post("/due-diligence/agreement", {
        deal_id: selectedDealId,
        ...agreementForm
      });
      setAgreement(data);
      toast.success("Due Diligence Agreement created!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create agreement");
    } finally {
      setCreatingAgreement(false);
    }
  };

  const updateChecklistItem = async (itemId, status, notes = "") => {
    try {
      await api.patch(`/due-diligence/checklist/${selectedDealId}/item/${itemId}`, {
        status,
        notes
      });
      fetchDealData();
      toast.success("Item updated");
    } catch (error) {
      toast.error("Failed to update item");
    }
  };

  const signAgreement = async () => {
    if (!signatureName.trim()) {
      toast.error("Please enter your name to sign");
      return;
    }
    
    setSigning(true);
    try {
      const role = user.id === agreement.buyer.id ? "buyer" : "seller";
      await api.post(`/due-diligence/agreement/${selectedDealId}/sign`, {
        signature_name: signatureName,
        signature_role: role
      });
      fetchDealData();
      setShowSignDialog(false);
      setSignatureName("");
      toast.success("Agreement signed successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sign agreement");
    } finally {
      setSigning(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const generatePDF = () => {
    if (!agreement) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;
    
    // Title
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("DUE DILIGENCE AGREEMENT", pageWidth / 2, y, { align: "center" });
    y += 15;
    
    // Subtitle
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Confidential Business Transaction Document", pageWidth / 2, y, { align: "center" });
    y += 20;
    
    // Agreement ID
    doc.setFontSize(9);
    doc.text(`Agreement ID: ${agreement.id}`, 20, y);
    y += 10;
    
    // Parties Section
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("PARTIES TO THIS AGREEMENT", 20, y);
    y += 10;
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`SELLER: ${agreement.seller.name}`, 20, y);
    y += 6;
    doc.text(`Email: ${agreement.seller.email}`, 20, y);
    y += 10;
    
    doc.text(`BUYER: ${agreement.buyer.name}`, 20, y);
    y += 6;
    doc.text(`Email: ${agreement.buyer.email}`, 20, y);
    y += 15;
    
    // Business Details
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("SUBJECT BUSINESS", 20, y);
    y += 10;
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Business Name: ${agreement.business_name}`, 20, y);
    y += 6;
    doc.text(`Industry: ${agreement.business_industry}`, 20, y);
    y += 6;
    doc.text(`Location: ${agreement.business_location}`, 20, y);
    y += 6;
    doc.text(`Proposed Deal Value: ${formatCurrency(agreement.deal_value)}`, 20, y);
    y += 15;
    
    // Terms
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("TERMS AND CONDITIONS", 20, y);
    y += 10;
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const terms = [
      `1. Effective Date: ${formatDate(agreement.terms.effective_date)}`,
      `2. Due Diligence Period: ${agreement.terms.due_diligence_period_days} days`,
      `3. Due Diligence End Date: ${formatDate(agreement.terms.due_diligence_end_date)}`,
      `4. Confidentiality Period: ${agreement.terms.confidentiality_period_months} months`,
      "",
      "5. CONFIDENTIALITY: Both parties agree to maintain strict confidentiality",
      "   regarding all business information shared during due diligence.",
      "",
      "6. ACCESS: Seller agrees to provide Buyer reasonable access to all",
      "   business records, documents, and premises as required.",
      "",
      "7. NON-DISCLOSURE: Neither party shall disclose any confidential",
      "   information to third parties without written consent.",
    ];
    
    terms.forEach(term => {
      doc.text(term, 20, y);
      y += 6;
    });
    
    if (agreement.terms.custom_terms) {
      y += 5;
      doc.text("8. ADDITIONAL TERMS:", 20, y);
      y += 6;
      const customLines = doc.splitTextToSize(agreement.terms.custom_terms, pageWidth - 40);
      customLines.forEach(line => {
        doc.text(`   ${line}`, 20, y);
        y += 6;
      });
    }
    
    y += 15;
    
    // Signatures
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("SIGNATURES", 20, y);
    y += 15;
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    
    // Seller signature
    doc.text("SELLER:", 20, y);
    y += 8;
    if (agreement.signatures.seller) {
      doc.text(`Signed by: ${agreement.signatures.seller.name}`, 20, y);
      y += 6;
      doc.text(`Date: ${formatDate(agreement.signatures.seller.signed_at)}`, 20, y);
    } else {
      doc.text("_______________________________", 20, y);
      y += 6;
      doc.text("(Pending)", 20, y);
    }
    y += 15;
    
    // Buyer signature
    doc.text("BUYER:", 20, y);
    y += 8;
    if (agreement.signatures.buyer) {
      doc.text(`Signed by: ${agreement.signatures.buyer.name}`, 20, y);
      y += 6;
      doc.text(`Date: ${formatDate(agreement.signatures.buyer.signed_at)}`, 20, y);
    } else {
      doc.text("_______________________________", 20, y);
      y += 6;
      doc.text("(Pending)", 20, y);
    }
    
    // Footer
    y = doc.internal.pageSize.getHeight() - 20;
    doc.setFontSize(8);
    doc.text("This document was generated by ReBusi - India's Premier Business Marketplace", pageWidth / 2, y, { align: "center" });
    
    doc.save(`DueDiligence_Agreement_${agreement.id.substring(0, 8)}.pdf`);
    toast.success("PDF downloaded!");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-20 pt-28 text-center">
          <ClipboardCheck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Due Diligence Center</h1>
          <p className="text-slate-600 mb-8">Please login to access due diligence tools</p>
          <Button onClick={() => navigate("/auth")} className="bg-orange-500 hover:bg-orange-600">
            Sign In to Continue
          </Button>
        </div>
      </div>
    );
  }

  const selectedDeal = deals.find(d => d.id === selectedDealId);
  const canSign = agreement && user && (
    (user.id === agreement.buyer.id && !agreement.signatures.buyer) ||
    (user.id === agreement.seller.id && !agreement.signatures.seller)
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <ClipboardCheck className="w-8 h-8 text-orange-500" />
              Due Diligence Center
            </h1>
            <p className="text-slate-600 mt-1">Manage checklists and agreements for your business deals</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : deals.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">No Active Deals</h2>
              <p className="text-slate-600 mb-6">
                You don't have any deals at Due Diligence stage (Stage 2+) yet.
              </p>
              <Button onClick={() => navigate("/marketplace")} variant="outline">
                Browse Marketplace
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Deal Selector Sidebar */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg">Your Deals</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  {deals.map((deal) => (
                    <div
                      key={deal.id}
                      onClick={() => setSelectedDealId(deal.id)}
                      className={`p-4 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                        selectedDealId === deal.id ? "bg-orange-50 border-l-4 border-l-orange-500" : ""
                      }`}
                    >
                      <h3 className="font-semibold text-slate-900 text-sm">{deal.listing_name}</h3>
                      <p className="text-xs text-slate-500">{deal.listing_industry}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          Stage {deal.current_stage}
                        </Badge>
                        {deal.has_checklist && (
                          <Badge className="text-xs bg-blue-100 text-blue-700">
                            {deal.checklist_completion}%
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {selectedDeal && (
                <Card className="mb-6">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-slate-900">{selectedDeal.listing_name}</h2>
                        <p className="text-slate-600">{selectedDeal.listing_industry} • {formatCurrency(selectedDeal.deal_value)}</p>
                      </div>
                      <Badge className="bg-orange-100 text-orange-700">
                        Deal Stage {selectedDeal.current_stage}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-6">
                  <TabsTrigger value="checklist" className="flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4" />
                    Checklist
                  </TabsTrigger>
                  <TabsTrigger value="agreement" className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Agreement
                  </TabsTrigger>
                </TabsList>

                {/* Checklist Tab */}
                <TabsContent value="checklist">
                  {!checklist ? (
                    <Card className="text-center py-12">
                      <CardContent>
                        <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">No Checklist Created</h3>
                        <p className="text-slate-600 mb-6">
                          Create a due diligence checklist to track document verification
                        </p>
                        <Button 
                          onClick={createChecklist} 
                          disabled={creatingChecklist}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          {creatingChecklist ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                          ) : (
                            "Create Checklist"
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <>
                      {/* Progress Bar */}
                      <Card className="mb-6">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-slate-700">Completion Progress</span>
                            <span className="text-sm font-bold text-orange-600">{checklist.completion_percentage}%</span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-3">
                            <div 
                              className="bg-orange-500 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${checklist.completion_percentage}%` }}
                            />
                          </div>
                        </CardContent>
                      </Card>

                      {/* Checklist Categories */}
                      {Object.entries(checklist.items).map(([category, items]) => {
                        const CategoryIcon = CATEGORY_ICONS[category] || ClipboardCheck;
                        const completedCount = items.filter(i => i.status === "verified" || i.status === "not_applicable").length;
                        
                        return (
                          <Card key={category} className="mb-4">
                            <CardHeader className="pb-3">
                              <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <CategoryIcon className="w-5 h-5 text-orange-500" />
                                  {CATEGORY_LABELS[category]}
                                </div>
                                <Badge variant="outline">
                                  {completedCount}/{items.length}
                                </Badge>
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-3">
                                {items.map((item) => {
                                  const statusConfig = STATUS_CONFIG[item.status];
                                  const StatusIcon = statusConfig.icon;
                                  
                                  return (
                                    <div 
                                      key={item.id}
                                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                                    >
                                      <div className="flex items-center gap-3 flex-1">
                                        <StatusIcon className={`w-5 h-5 ${
                                          item.status === "verified" ? "text-emerald-500" :
                                          item.status === "flagged" ? "text-red-500" :
                                          "text-slate-400"
                                        }`} />
                                        <div className="flex-1">
                                          <p className="text-sm font-medium text-slate-900">
                                            {item.item}
                                            {item.required && <span className="text-red-500 ml-1">*</span>}
                                          </p>
                                          {item.notes && (
                                            <p className="text-xs text-slate-500 mt-1">{item.notes}</p>
                                          )}
                                          {item.verified_by && (
                                            <p className="text-xs text-emerald-600 mt-1">
                                              Verified by {item.verified_by} on {formatDate(item.verified_at)}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                      <Select
                                        value={item.status}
                                        onValueChange={(val) => updateChecklistItem(item.id, val)}
                                      >
                                        <SelectTrigger className={`w-36 ${statusConfig.color}`}>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="pending">Pending</SelectItem>
                                          <SelectItem value="verified">Verified</SelectItem>
                                          <SelectItem value="flagged">Flagged</SelectItem>
                                          <SelectItem value="not_applicable">N/A</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  );
                                })}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </>
                  )}
                </TabsContent>

                {/* Agreement Tab */}
                <TabsContent value="agreement">
                  {!agreement ? (
                    <Card>
                      <CardHeader>
                        <CardTitle>Create Due Diligence Agreement</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Due Diligence Period (Days)</Label>
                            <Input
                              type="number"
                              value={agreementForm.due_diligence_period_days}
                              onChange={(e) => setAgreementForm(prev => ({
                                ...prev,
                                due_diligence_period_days: parseInt(e.target.value) || 30
                              }))}
                              min={7}
                              max={90}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Confidentiality Period (Months)</Label>
                            <Input
                              type="number"
                              value={agreementForm.confidentiality_period_months}
                              onChange={(e) => setAgreementForm(prev => ({
                                ...prev,
                                confidentiality_period_months: parseInt(e.target.value) || 24
                              }))}
                              min={6}
                              max={60}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Custom Terms (Optional)</Label>
                          <Textarea
                            value={agreementForm.custom_terms}
                            onChange={(e) => setAgreementForm(prev => ({
                              ...prev,
                              custom_terms: e.target.value
                            }))}
                            placeholder="Add any additional terms or conditions..."
                            rows={4}
                          />
                        </div>
                        <Button 
                          onClick={createAgreement}
                          disabled={creatingAgreement}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          {creatingAgreement ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                          ) : (
                            "Generate Agreement"
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <>
                      {/* Agreement Status */}
                      <Card className="mb-6">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileCheck className={`w-8 h-8 ${
                                agreement.status === "fully_executed" ? "text-emerald-500" : "text-amber-500"
                              }`} />
                              <div>
                                <h3 className="font-semibold text-slate-900">Agreement Status</h3>
                                <p className="text-sm text-slate-600">
                                  {agreement.status === "fully_executed" ? "Fully Executed" :
                                   agreement.status === "partially_signed" ? "Partially Signed" :
                                   "Pending Signatures"}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="outline" onClick={generatePDF}>
                                <Download className="w-4 h-4 mr-2" />
                                Download PDF
                              </Button>
                              {canSign && (
                                <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
                                  <DialogTrigger asChild>
                                    <Button className="bg-orange-500 hover:bg-orange-600">
                                      <Pen className="w-4 h-4 mr-2" />
                                      Sign Agreement
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent>
                                    <DialogHeader>
                                      <DialogTitle>Sign Due Diligence Agreement</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4">
                                      <p className="text-sm text-slate-600">
                                        By signing this agreement, you confirm that you have read and agree to all terms and conditions.
                                      </p>
                                      <div className="space-y-2">
                                        <Label>Your Full Name (as signature)</Label>
                                        <Input
                                          value={signatureName}
                                          onChange={(e) => setSignatureName(e.target.value)}
                                          placeholder="Enter your full legal name"
                                        />
                                      </div>
                                      <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
                                        <Shield className="w-5 h-5 text-amber-600" />
                                        <p className="text-sm text-amber-700">
                                          This signature is legally binding and will be recorded with timestamp.
                                        </p>
                                      </div>
                                      <Button 
                                        onClick={signAgreement}
                                        disabled={signing || !signatureName.trim()}
                                        className="w-full bg-orange-500 hover:bg-orange-600"
                                      >
                                        {signing ? (
                                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing...</>
                                        ) : (
                                          "Confirm & Sign"
                                        )}
                                      </Button>
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Agreement Document Preview */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-orange-500" />
                            Due Diligence Agreement
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="prose prose-sm max-w-none">
                          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-6">
                            {/* Header */}
                            <div className="text-center border-b border-slate-200 pb-4">
                              <h2 className="text-xl font-bold text-slate-900">DUE DILIGENCE AGREEMENT</h2>
                              <p className="text-sm text-slate-500">Confidential Business Transaction Document</p>
                              <p className="text-xs text-slate-400 mt-2">Agreement ID: {agreement.id}</p>
                            </div>

                            {/* Parties */}
                            <div>
                              <h3 className="font-semibold text-slate-900 mb-3">PARTIES TO THIS AGREEMENT</h3>
                              <div className="grid md:grid-cols-2 gap-4">
                                <div className="p-4 bg-slate-50 rounded-lg">
                                  <p className="text-xs text-slate-500 uppercase tracking-wider">Seller</p>
                                  <p className="font-semibold text-slate-900">{agreement.seller.name}</p>
                                  <p className="text-sm text-slate-600">{agreement.seller.email}</p>
                                </div>
                                <div className="p-4 bg-slate-50 rounded-lg">
                                  <p className="text-xs text-slate-500 uppercase tracking-wider">Buyer</p>
                                  <p className="font-semibold text-slate-900">{agreement.buyer.name}</p>
                                  <p className="text-sm text-slate-600">{agreement.buyer.email}</p>
                                </div>
                              </div>
                            </div>

                            {/* Business Details */}
                            <div>
                              <h3 className="font-semibold text-slate-900 mb-3">SUBJECT BUSINESS</h3>
                              <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                                <div className="grid md:grid-cols-2 gap-4">
                                  <div>
                                    <p className="text-xs text-slate-500">Business Name</p>
                                    <p className="font-semibold text-slate-900">{agreement.business_name}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-500">Industry</p>
                                    <p className="font-semibold text-slate-900">{agreement.business_industry}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-500">Location</p>
                                    <p className="font-semibold text-slate-900">{agreement.business_location}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-500">Proposed Deal Value</p>
                                    <p className="font-semibold text-orange-600">{formatCurrency(agreement.deal_value)}</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Terms */}
                            <div>
                              <h3 className="font-semibold text-slate-900 mb-3">TERMS AND CONDITIONS</h3>
                              <div className="space-y-3 text-sm text-slate-700">
                                <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                                  <div>
                                    <p className="text-xs text-slate-500">Effective Date</p>
                                    <p className="font-medium">{formatDate(agreement.terms.effective_date)}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-500">Due Diligence Period</p>
                                    <p className="font-medium">{agreement.terms.due_diligence_period_days} days</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-500">Due Diligence End Date</p>
                                    <p className="font-medium">{formatDate(agreement.terms.due_diligence_end_date)}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-500">Confidentiality Period</p>
                                    <p className="font-medium">{agreement.terms.confidentiality_period_months} months</p>
                                  </div>
                                </div>
                                
                                <ol className="list-decimal list-inside space-y-2 pl-2">
                                  <li><strong>CONFIDENTIALITY:</strong> Both parties agree to maintain strict confidentiality regarding all business information shared during the due diligence period.</li>
                                  <li><strong>ACCESS:</strong> The Seller agrees to provide the Buyer reasonable access to all business records, documents, and premises as reasonably required for due diligence.</li>
                                  <li><strong>NON-DISCLOSURE:</strong> Neither party shall disclose any confidential information to third parties without prior written consent of the other party.</li>
                                  <li><strong>NON-SOLICITATION:</strong> The Buyer agrees not to solicit or hire any employees of the Seller's business during and for 12 months after the due diligence period.</li>
                                  <li><strong>GOOD FAITH:</strong> Both parties agree to conduct all negotiations and due diligence activities in good faith.</li>
                                </ol>

                                {agreement.terms.custom_terms && (
                                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                                    <p className="text-xs text-amber-600 uppercase tracking-wider mb-2">Additional Terms</p>
                                    <p className="text-slate-700">{agreement.terms.custom_terms}</p>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Signatures */}
                            <div>
                              <h3 className="font-semibold text-slate-900 mb-3">SIGNATURES</h3>
                              <div className="grid md:grid-cols-2 gap-4">
                                <div className={`p-4 rounded-lg border-2 ${
                                  agreement.signatures.seller ? "border-emerald-200 bg-emerald-50" : "border-dashed border-slate-300"
                                }`}>
                                  <p className="text-xs text-slate-500 uppercase tracking-wider">Seller Signature</p>
                                  {agreement.signatures.seller ? (
                                    <>
                                      <p className="font-semibold text-slate-900 mt-2 text-lg italic">
                                        {agreement.signatures.seller.name}
                                      </p>
                                      <p className="text-xs text-emerald-600 mt-1">
                                        Signed on {formatDate(agreement.signatures.seller.signed_at)}
                                      </p>
                                    </>
                                  ) : (
                                    <p className="text-slate-400 mt-2 italic">Pending signature...</p>
                                  )}
                                </div>
                                <div className={`p-4 rounded-lg border-2 ${
                                  agreement.signatures.buyer ? "border-emerald-200 bg-emerald-50" : "border-dashed border-slate-300"
                                }`}>
                                  <p className="text-xs text-slate-500 uppercase tracking-wider">Buyer Signature</p>
                                  {agreement.signatures.buyer ? (
                                    <>
                                      <p className="font-semibold text-slate-900 mt-2 text-lg italic">
                                        {agreement.signatures.buyer.name}
                                      </p>
                                      <p className="text-xs text-emerald-600 mt-1">
                                        Signed on {formatDate(agreement.signatures.buyer.signed_at)}
                                      </p>
                                    </>
                                  ) : (
                                    <p className="text-slate-400 mt-2 italic">Pending signature...</p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Footer */}
                            <div className="text-center pt-4 border-t border-slate-200">
                              <p className="text-xs text-slate-400">
                                This document was generated by ReBusi - India's Premier Business Marketplace
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
